import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

const API_URL = process.env.REACT_APP_API_URL || "";

const APP_STATUS_FLOW = [
  { value: "INVIATO", label: "Ricevuto" },
  { value: "IN_PREPARAZIONE", label: "In preparazione" },
  { value: "PRONTO_RITIRO", label: "Pronto al ritiro" },
  { value: "RITIRATO", label: "Ritirato" },
];

export default function OrdineAppDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [ordine, setOrdine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem("access") || "";

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore caricamento ordine");
      setOrdine(data);
    } catch (e) {
      setErr(e.message || "Errore");
      setOrdine(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (nextStatus) => {
    setUpdating(true);
    setErr("");
    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/app-status/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ app_status: nextStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore aggiornamento stato");
      await load();
    } catch (e) {
      setErr(e.message || "Errore aggiornamento stato");
    } finally {
      setUpdating(false);
    }
  };

  const printOrder = async () => {
    setErr("");

    try {
      const res = await fetch(`${API_URL}/api/ordini/${id}/print/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Errore generazione PDF ordine");
      }

      const blob = await res.blob();

      if (window.Capacitor?.isNativePlatform?.()) {
        const reader = new FileReader();

        const base64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => {
            const result = String(reader.result || "");
            resolve(result.split(",")[1] || "");
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const fileName = `ordine-${id}.pdf`;

        const saved = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });

        await Share.share({
          title: `Ordine #${id}`,
          text: `Ordine #${id}`,
          url: saved.uri,
          dialogTitle: "Apri o stampa ordine",
        });

        return;
      }

      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (e) {
      setErr(e.message || "Errore generazione PDF ordine");
    }
  };
  const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

  const formatDateOnlyIT = (value) => {
    if (!value) return "-";
    const raw = String(value);
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return raw;
  };

  const formatTimeIT = (value) => {
    if (!value) return "-";
    const raw = String(value);
    const m = raw.match(/^(\d{2}):(\d{2})/);
    if (m) return `${m[1]}:${m[2]}`;
    return raw;
  };

  if (loading) {
    return <div className="container mt-4">Caricamento…</div>;
  }

  if (err && !ordine) {
    return (
      <div className="container mt-4">
        <button className="btn btn-outline-secondary mb-3" onClick={() => nav(-1)}>← Torna</button>
        <div className="alert alert-danger">{err}</div>
      </div>
    );
  }

  const activeStatus = String(ordine?.app_status || "INVIATO").toUpperCase();
  const items = ordine?.items || [];

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => nav(-1)}
        >
          Torna
        </button>

        <button
          type="button"
          className="btn btn-danger fw-semibold"
          onClick={printOrder}
        >
          Stampa ordine
        </button>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="card shadow-sm border-0 rounded-4 mb-3">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="text-muted small">Ordine app cliente</div>
              <h2 className="mb-1">#{ordine.id}</h2>
              <div className="d-flex flex-wrap gap-2">
                <span className="badge bg-success">{ordine.app_status_label || ordine.app_status}</span>
                <span className={ordine.is_paid ? "badge bg-success" : "badge bg-warning text-dark"}>
                  {ordine.is_paid ? "Pagato" : "Non pagato"}
                </span>
                <span className="badge bg-light text-dark border">{ordine.payment_method || "-"}</span>
              </div>
            </div>

            <div className="text-end">
              <div className="text-muted small">Totale</div>
              <div className="display-6 fw-bold">{money(ordine.total)}</div>
              <div className="small text-muted">{ordine.date || "-"}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm border-0 rounded-4 h-100">
            <div className="card-body p-4">
              <div className="text-muted small mb-1">Cliente</div>
              <h4 className="mb-2">{ordine.cliente || ordine.per_nome || "-"}</h4>
              <div className="small text-muted">Email</div>
              <div className="mb-2">{ordine.sender_username || "-"}</div>
              <div className="small text-muted">Codice cliente</div>
              <div className="fw-semibold">{ordine.per_nome || "-"}</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0 rounded-4 h-100">
            <div className="card-body p-4">
              <div className="alert alert-info border-0 rounded-4 mb-3">
                <div className="small text-muted">Ritiro previsto</div>
                <div className="fw-bold">
                  {formatDateOnlyIT(ordine.pickup_date)} — {formatTimeIT(ordine.pickup_time_from)} / {formatTimeIT(ordine.pickup_time_to)}
                </div>
              </div>

                <div className="mb-3">
                  <h4 className="mb-1">Stato ordine</h4>
                  <div className="text-muted small">
                    Aggiornato automaticamente quando il punto vendita cambia lo stato.
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-start gap-2">
                  {APP_STATUS_FLOW.map((st, idx) => {
                    const activeIdx = APP_STATUS_FLOW.findIndex((x) => x.value === activeStatus);
                    const active = idx === Math.max(activeIdx, 0);
                    return (
                      <div className="text-center flex-fill" key={st.value}>
                        <button
                          type="button"
                          className="mx-auto rounded-circle d-flex align-items-center justify-content-center"
                          disabled={updating || st.value === activeStatus}
                          onClick={() => updateStatus(st.value)}
                          title={`Imposta stato: ${st.label}`}
                          style={{
                            width: 34,
                            height: 34,
                            fontWeight: 800,
                            background: active ? "#5b0f1b" : "#f8f1ed",
                            color: active ? "#fff" : "#8a6b70",
                            border: active ? "0" : "1px solid rgba(91,15,27,0.18)",
                            cursor:
                              updating || st.value === activeStatus
                                ? "default"
                                : "pointer",
                            opacity: updating ? 0.7 : 1,
                            padding: 0,
                          }}
                        >
                          {idx + 1}
                        </button>
                        <div
                          className="small mt-2"
                          style={{ color: active ? "#211014" : "#8a6b70", fontWeight: active ? 800 : 500 }}
                        >
                          {st.label}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="progress mt-3" style={{ height: 8, background: "#f0e4de" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${((Math.max(APP_STATUS_FLOW.findIndex((x) => x.value === activeStatus), 0) + 1) / APP_STATUS_FLOW.length) * 100}%`,
                      background: "#5b0f1b"
                    }}
                  />
                </div>

            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-body p-4">
              <h4 className="mb-3">Prodotti ordinati</h4>

              <div className="table-responsive">
                <table className="table align-middle" style={{ minWidth: "720px", whiteSpace: "nowrap" }}>
                  <thead>
                    <tr>
                      <th>Prodotto</th>
                      <th className="text-end">Q.tà</th>
                      <th>Formato</th>
                      <th>Contenitore</th>
                      <th className="text-end">Prezzo</th>
                      <th className="text-end">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={`${it.product}-${idx}`}>
                        <td className="fw-semibold">{it.product_name || "-"}</td>
                        <td className="text-end">{Number(it.quantity || 0).toFixed(2)}</td>
                        <td>{it.formato || "—"}</td>
                        <td>{it.contenitore || "—"}</td>
                        <td className="text-end">{money(it.unit_price)}</td>
                        <td className="text-end">{money(it.line_subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(ordine.customer_note || ordine.messaggio_agente) && (
                <div className="alert alert-light border mt-3 mb-0">
                  <div className="small text-muted">Note cliente</div>
                  {ordine.customer_note || ordine.messaggio_agente}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
